import { useEffect, useRef } from "react";

// Metaball "thinking" indicator: 4 droplets orbit and merge into one blob,
// rendered as a scalar-field isoline on canvas (artifact-free liquid look).
const SS = 2;            // supersample for smooth edges
const SIZE = 32;         // css px
const N = 3;             // droplet count
const T = 1.45;          // field threshold (stickiness)
const EDGE = 20;         // edge sharpness
const RSCALE = 1.02;     // droplet radius
const SPREAD = 1.12;     // orbit radius
const MAXF = 1.8;        // max orbit factor
const PERIOD = 1.0;      // seconds per cycle (base 2.0s @ 2×)
const TAU = Math.PI * 2;
const sine = (ph: number) => (1 - Math.cos(ph * TAU)) / 2;

export function StreamingPulse() {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    if (!ctx) return;

    const w = SIZE * SS, h = SIZE * SS;
    cv.width = w;
    cv.height = h;
    const img = ctx.createImageData(w, h);
    const data = img.data;

    // ink color inherited from CSS `color` (theme --c-text)
    const m = getComputedStyle(cv).color.match(/\d+/g);
    const ink: [number, number, number] = [
      m ? +(m[0] ?? 10) : 10,
      m ? +(m[1] ?? 10) : 10,
      m ? +(m[2] ?? 10) : 10,
    ];

    const U = 0.1 * w;
    const r = U * RSCALE;
    const rsq = r * r;
    const edge = EDGE / SS;
    const cx = w / 2, cy = h / 2;

    let raf = 0, start = 0;
    const frame = (ts: number) => {
      if (!start) start = ts;
      const ph = (((ts - start) / 1000) / PERIOD) % 1;
      const base = ph * TAU;
      const rr = MAXF * sine(ph) * U * SPREAD;
      const bx = new Float64Array(N), by = new Float64Array(N);
      for (let k = 0; k < N; k++) {
        const a = base + k * (TAU / N);
        bx[k] = cx + Math.cos(a) * rr;
        by[k] = cy + Math.sin(a) * rr;
      }
      let p = 0;
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          let f = 0;
          for (let i = 0; i < N; i++) {
            const dx = x - bx[i]!, dy = y - by[i]!;
            f += rsq / (dx * dx + dy * dy + 0.01);
          }
          let a = (f - T) * edge + 0.5;
          a = a < 0 ? 0 : a > 1 ? 1 : a;
          data[p] = ink[0]; data[p + 1] = ink[1]; data[p + 2] = ink[2];
          data[p + 3] = (a * 255) | 0;
          p += 4;
        }
      }
      ctx.putImageData(img, 0, 0);
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div className="streaming-pulse" role="status" aria-label="Генерирую ответ">
      <canvas ref={ref} />
    </div>
  );
}
